// ─────────────────────────────────────────────────────────────────────
//  Demo-mode seed data
// ─────────────────────────────────────────────────────────────────────
//  Used as a fallback when the backend is unreachable. Shapes mirror what
//  `/api/properties` / `/api/featured/properties` return so PropertyCard,
//  Search, and PropertyDetail can consume them without conditional logic.
// ─────────────────────────────────────────────────────────────────────

const img = (path) => `https://images.unsplash.com/${path}?auto=format&fit=crop&w=1600&q=80`;

export const DEMO_PROPERTIES = [
  {
    property_id: 'demo-1',
    title: 'Cliffside Villa with Infinity Pool',
    description:
      'A sculpted three-bedroom villa with 180° ocean views, open-plan living, and a cantilevered infinity pool. Minutes from the marina and boutique dining.',
    property_type: 'villa',
    monthly_rent: 8500,
    currency: 'USD',
    billing_period: 'monthly',
    deposit: 17000,
    negotiable: true,
    owner_verified: true,
    status: 'active',
    is_favorite: false,
    location: {
      area: 'Beverly Glen',
      city: 'Los Angeles',
      country: 'USA',
      latitude: 34.1197,
      longitude: -118.4158,
    },
    details: { bedroom_count: 3, bathroom_count: 3, area_sqft: 3200 },
    amenities: ['Pool', 'Ocean View', 'Parking', 'Smart Home', 'Gym'],
    media: [
      { url: img('photo-1613490493576-7fde63acd811'), type: 'image' },
      { url: img('photo-1600596542815-ffad4c1539a9'), type: 'image' },
    ],
    created_at: '2026-03-18T10:00:00Z',
  },
  {
    property_id: 'demo-2',
    title: 'Soho Loft with Original Brick',
    description:
      'A sun-drenched 2BR loft in the heart of Soho. Exposed brick, 14-foot ceilings, and a chef\'s kitchen with Miele appliances.',
    property_type: 'apartment',
    monthly_rent: 6200,
    currency: 'USD',
    billing_period: 'monthly',
    deposit: 12400,
    negotiable: false,
    owner_verified: true,
    status: 'active',
    is_favorite: false,
    location: {
      area: 'Soho',
      city: 'New York',
      country: 'USA',
      latitude: 40.7233,
      longitude: -74.0024,
    },
    details: { bedroom_count: 2, bathroom_count: 2, area_sqft: 1450 },
    amenities: ['Doorman', 'Elevator', 'Washer/Dryer', 'Exposed Brick'],
    media: [
      { url: img('photo-1560448204-e02f11c3d0e2'), type: 'image' },
      { url: img('photo-1522708323590-d24dbb6b0267'), type: 'image' },
    ],
    created_at: '2026-03-21T09:30:00Z',
  },
  {
    property_id: 'demo-3',
    title: 'Marina Penthouse with Burj View',
    description:
      'Top-floor penthouse in Dubai Marina with panoramic Burj Khalifa and Palm Jumeirah views. Private plunge pool on terrace.',
    property_type: 'penthouse',
    monthly_rent: 12500,
    currency: 'USD',
    billing_period: 'monthly',
    deposit: 25000,
    negotiable: true,
    owner_verified: true,
    status: 'active',
    is_favorite: false,
    location: {
      area: 'Dubai Marina',
      city: 'Dubai',
      country: 'UAE',
      latitude: 25.0811,
      longitude: 55.1404,
    },
    details: { bedroom_count: 4, bathroom_count: 5, area_sqft: 4100 },
    amenities: ['Concierge', 'Gym', 'Private Pool', 'Valet', 'Marina Access'],
    media: [
      { url: img('photo-1582268611958-ebfd161ef9cf'), type: 'image' },
      { url: img('photo-1512453979798-5ea266f8880c'), type: 'image' },
    ],
    created_at: '2026-03-12T14:00:00Z',
  },
  {
    property_id: 'demo-4',
    title: 'Haussmann Apartment off Avenue Montaigne',
    description:
      'Classic Haussmannian 2-bedroom with parquet floors, marble fireplace, and iron balcony overlooking a quiet courtyard off Avenue Montaigne.',
    property_type: 'apartment',
    monthly_rent: 4800,
    currency: 'EUR',
    billing_period: 'monthly',
    deposit: 9600,
    negotiable: false,
    owner_verified: true,
    status: 'active',
    is_favorite: false,
    location: {
      area: '8th Arrondissement',
      city: 'Paris',
      country: 'France',
      latitude: 48.8675,
      longitude: 2.3080,
    },
    details: { bedroom_count: 2, bathroom_count: 2, area_sqft: 1200 },
    amenities: ['Elevator', 'Courtyard View', 'Fireplace', 'Historic'],
    media: [
      { url: img('photo-1502602898657-3e91760cbb34'), type: 'image' },
      { url: img('photo-1556909114-f6e7ad7d3136'), type: 'image' },
    ],
    created_at: '2026-04-02T08:15:00Z',
  },
  {
    property_id: 'demo-5',
    title: 'Minimalist Townhouse in Shibuya',
    description:
      'Architect-designed 3-story townhouse a 6-minute walk from Shibuya Station. Floor-to-ceiling glass, cedar accents, and a private zen garden.',
    property_type: 'house',
    monthly_rent: 580000,
    currency: 'JPY',
    billing_period: 'monthly',
    deposit: 1160000,
    negotiable: true,
    owner_verified: true,
    status: 'active',
    is_favorite: false,
    location: {
      area: 'Shibuya',
      city: 'Tokyo',
      country: 'Japan',
      latitude: 35.6595,
      longitude: 139.7005,
    },
    details: { bedroom_count: 3, bathroom_count: 2, area_sqft: 1850 },
    amenities: ['Garden', 'Architect-Designed', 'Smart Home', 'Parking'],
    media: [
      { url: img('photo-1540959733332-eab4deabeeaf'), type: 'image' },
      { url: img('photo-1503899036084-c55cdd92da26'), type: 'image' },
    ],
    created_at: '2026-04-10T11:45:00Z',
  },
  {
    property_id: 'demo-6',
    title: 'Notting Hill Mews House',
    description:
      'Converted mews on a quiet cobbled street. Two bedrooms, a skylit study, and a hidden rooftop terrace with London skyline views.',
    property_type: 'house',
    monthly_rent: 4200,
    currency: 'GBP',
    billing_period: 'monthly',
    deposit: 8400,
    negotiable: false,
    owner_verified: true,
    status: 'active',
    is_favorite: false,
    location: {
      area: 'Notting Hill',
      city: 'London',
      country: 'United Kingdom',
      latitude: 51.5106,
      longitude: -0.2060,
    },
    details: { bedroom_count: 2, bathroom_count: 2, area_sqft: 1400 },
    amenities: ['Rooftop Terrace', 'Skylight', 'Cobbled Street', 'Parking'],
    media: [
      { url: img('photo-1513635269975-59663e0ac1ad'), type: 'image' },
      { url: img('photo-1600585154340-be6161a56a0c'), type: 'image' },
    ],
    created_at: '2026-04-15T16:20:00Z',
  },
];

export const DEMO_CITIES = [
  { city: 'New York', country: 'USA', property_count: 1248 },
  { city: 'London', country: 'United Kingdom', property_count: 892 },
  { city: 'Dubai', country: 'UAE', property_count: 643 },
  { city: 'Paris', country: 'France', property_count: 512 },
  { city: 'Tokyo', country: 'Japan', property_count: 428 },
  { city: 'Los Angeles', country: 'USA', property_count: 387 },
];

// Filter helper so Search can narrow by city/query without a real backend.
export function filterDemoProperties({ q, city, minPrice, maxPrice, bedrooms } = {}) {
  return DEMO_PROPERTIES.filter((p) => {
    if (city && !p.location.city.toLowerCase().includes(city.toLowerCase())) return false;
    if (q) {
      const haystack = [
        p.title, p.description,
        p.location.area, p.location.city, p.location.country,
      ].join(' ').toLowerCase();
      if (!haystack.includes(q.toLowerCase())) return false;
    }
    if (minPrice != null && p.monthly_rent < minPrice) return false;
    if (maxPrice != null && p.monthly_rent > maxPrice) return false;
    if (bedrooms != null && p.details.bedroom_count < bedrooms) return false;
    return true;
  });
}

export function isDemoMode() {
  return localStorage.getItem('token') === 'demo-session-token';
}
